import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';

// L-01 (MA-06): the JWT lives in an httpOnly cookie and is no longer readable by JS.
// The non-httpOnly `csrfToken` cookie (set by the backend at login/verifyToken) is the
// only client-readable signal that a session might exist, so it stands in for the old
// "is there a token in storage" check below — same gating logic, different source.

function ProtectedRoute({
  authenticated,
  isBusiness,
  isSessionVerifying: parentSessionVerifying,
  children,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSessionVerifying, setIsSessionVerifying] = useState(true);

  useEffect(() => {
    // Check if there's a plausible session (csrfToken cookie) to determine if
    // session verification is needed
    const checkSessionStatus = () => {
      try {
        const csrfToken = Cookies.get("csrfToken");
        if (csrfToken) {
          // There's a plausible session, wait for session verification to complete
          setIsSessionVerifying(true);
          // Set a timeout to prevent infinite loading if verification takes too long
          const timeoutId = setTimeout(() => {
            console.warn(
              "Session verification timeout - proceeding with current state"
            );
            setIsSessionVerifying(false);
          }, 5000); // 5 second timeout

          // Cleanup timeout if component unmounts
          return () => clearTimeout(timeoutId);
        } else {
          // No plausible session, session verification not needed
          setIsSessionVerifying(false);
        }
      } catch (error) {
        console.error("Error checking session status:", error);
        setIsSessionVerifying(false);
      }
    };

    checkSessionStatus();
  }, []);

  // Use the parent's session verification state if provided
  const shouldWaitForSession =
    parentSessionVerifying !== undefined
      ? parentSessionVerifying
      : isSessionVerifying;

  useEffect(() => {
    // Only proceed with authentication check after session verification is complete
    if (shouldWaitForSession) {
      console.log("🔒 ProtectedRoute: Waiting for session verification...");
      return;
    }

    console.log(
      "🔒 ProtectedRoute: Session verification complete, checking auth state:",
      {
        authenticated,
        isBusiness,
        pathname: location.pathname,
      }
    );

    if (!authenticated || !isBusiness) {
      console.log(
        "🔒 ProtectedRoute: User not authenticated, redirecting to login"
      );

      // Guardar la URL actual en localStorage para redirigir después del login
      localStorage.setItem(
        "mantis_redirect_url",
        location.pathname + location.search
      );

      // Mostrar mensaje informativo
      localStorage.setItem("mantis_login_required", "true");

      // Pequeño delay para mostrar el mensaje de redirección
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 1000);
    } else {
      console.log(
        "🔒 ProtectedRoute: User authenticated, rendering protected content"
      );
    }
  }, [authenticated, isBusiness, navigate, location, shouldWaitForSession]);

  // Show loading state while session verification is in progress
  if (shouldWaitForSession) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Verificando sesión...
          </h2>
          <p className="text-gray-600 mb-4">
            Por favor espera mientras verificamos tu sesión.
          </p>
        </div>
      </div>
    );
  }

  // Fallback check: if Redux says not authenticated but there's a plausible session
  // (csrfToken cookie), wait a bit more for the state to sync
  const hasPlausibleSession = (() => {
    try {
      return !!Cookies.get("csrfToken");
    } catch (error) {
      return false;
    }
  })();

  if (!authenticated && hasPlausibleSession) {
    console.log("🔒 ProtectedRoute: Redux state not synced yet, waiting...");
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Sincronizando estado...
          </h2>
          <p className="text-gray-600 mb-4">
            Por favor espera mientras sincronizamos tu sesión.
          </p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, mostrar mensaje de redirección
  if (!authenticated || !isBusiness) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Acceso Requerido
          </h2>
          <p className="text-gray-600 mb-4">
            Necesitas iniciar sesión para acceder a esta página.
          </p>
          <p className="text-sm text-gray-500">
            Serás redirigido al login y después regresarás aquí automáticamente.
          </p>
        </div>
      </div>
    );
  }

  // Si está autenticado, renderizar los children
  return children;
}

const mapStateToProps = (state) => ({
  authenticated: state.auth.authenticated,
  isBusiness: state.auth.isBusiness,
});

export default connect(mapStateToProps)(ProtectedRoute); 